import { Link, Navigate } from "react-router-dom";
import { useState } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { HomeTopNav } from "@/components/portal/HomeTopNav";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  getForgotPasswordErrorMessage,
  getForgotPasswordSuccessMessage,
  isForgotPasswordFormValid,
  toForgotPasswordRequest,
} from "@/lib/api/forgot-password-form";
import { requestPasswordReset } from "@/lib/api/forgot-password";
import { getAuthSession } from "@/lib/auth/session";

export default function ForgotPasswordPage() {
  usePageTitle("Forgot password");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (getAuthSession()) {
    return <Navigate to="/" replace />;
  }

  const canSubmit = isForgotPasswordFormValid({ email });

  async function handleSubmit(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!canSubmit || loading || Boolean(success)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await requestPasswordReset(toForgotPasswordRequest({ email }));
      setSuccess(getForgotPasswordSuccessMessage(response));
    } catch (err: unknown) {
      setError(getForgotPasswordErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <HomeTopNav />
      <main id="main-content" className="auth-shell">
        <div style={{ width: "100%", maxWidth: 440 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <AbcFitnessBrandLogo to="" theme="light" />
            <div
              style={{
                fontWeight: 800,
                fontSize: 16,
                color: "var(--t1)",
                marginTop: ".75rem",
              }}
            >
              ABC Fitness Developer Portal
            </div>
          </div>

          <div className="auth-card" data-testid="home-forgot-password-card">
            <div
              className="auth-hdr"
              style={{ background: "var(--slate-dark)" }}
            >
              <p className="auth-eye">Password reset</p>
              <h1 data-testid="home-forgot-password-title">Forgot your password?</h1>
              <p>
                Enter your email and we&apos;ll send you a link to choose a new
                password.
              </p>
            </div>
            <div className="auth-body">
              {success && (
                <div
                  className="success-msg"
                  data-testid="home-forgot-password-success"
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "12px 14px",
                    borderRadius: 8,
                    background: "rgba(0, 181, 136, 0.08)",
                    color: "var(--t1)",
                    fontSize: 13,
                    marginBottom: "1rem",
                  }}
                >
                  <i className="ti ti-circle-check" style={{ color: "var(--abc)" }} />
                  <span>{success}</span>
                </div>
              )}

              <form
                className="auth-form"
                data-testid="home-forgot-password-form"
                noValidate
                onSubmit={(event) => {
                  void handleSubmit(event);
                }}
              >
              {error && (
                <div className="error-msg" data-testid="home-forgot-password-error" role="alert">
                  <i className="ti ti-alert-circle" />
                  <span>{error}</span>
                </div>
              )}

              <div className="field">
                <label htmlFor="home-forgot-password-email">Email address</label>
                <input
                  id="home-forgot-password-email"
                  type="email"
                  data-testid="home-forgot-password-email"
                  placeholder="you@company.com"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                    setSuccess(null);
                  }}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                data-testid="home-forgot-password-submit"
                disabled={loading || !canSubmit || Boolean(success)}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: ".25rem",
                  opacity: loading || !canSubmit || success ? 0.5 : 1,
                }}
              >
                {loading ? (
                  <>
                    <i
                      className="ti ti-loader-2"
                      style={{ animation: "spin 1s linear infinite" }}
                    />{" "}
                    Sending…
                  </>
                ) : (
                  <>
                    <i className="ti ti-mail" /> Send reset link
                  </>
                )}
              </button>
              </form>
            </div>
          </div>

          <p className="already">
            Remember your password?{" "}
            <Link to="/login" data-testid="home-forgot-password-login-link">
              Log in →
            </Link>
          </p>
        </div>
      </main>
    </>
  );
}
